import { UserRecord } from "firebase-functions/v1/auth";
import { browserLocalPersistence, setPersistence, signInWithEmailAndPassword, signOut, Unsubscribe, User } from "firebase/auth";
import { collection, onSnapshot, query, QueryConstraint, Timestamp, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { cloneDeep } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fireAuth, fireDb, fireFunctions } from "./firebase-config";
import { DBUser, HouseName, ReservationStatus } from "./ostertalertypes";
import { getAllUsers, getIncomingRequests, getUserDetails, rescueDeletedUser } from "./utils/database";
import { dateToUTC } from "./utils/dateutils";
import { getAdminClaims } from "./utils/adminutils";

interface IAuthContextProps {
  user: User | null;
  userDetails: DBUser | null;
  logout: () => void;
  login: (email: string, password: string) => void;
  registerAccount: (email: string, password: string, firstName: string, lastName: string, phone: string | undefined) => Promise<UserRecord>;
  addAnonymAccountToCache: (userid: string, firstName: string, lastName: string) => void;
  deleteUser: (user: DBUser) => Promise<void>;
  isAdmin: () => boolean;
  isLoggedIn: () => boolean;
  allUsers: DBUser[];
  countIncomingBookings: number;
  isUserExisting: (firstname: string, lastname: string) => boolean;
  updateUser: (user:any) => Promise<void>;
  adminHouses: HouseName[];
}

export type LocationState = {
  from: string;
};

interface IAuthState {
  user: User | null;
  userDetails: DBUser | null;
  pending: boolean;
  admin: boolean;
  adminHouses: HouseName[];
}

const defaultContextState = {
  user: null,
  userDetails: null,
  logout: () => Promise.resolve(),
  login: (email: string, password: string) => Promise.resolve(),
  registerAccount: (email: string, password: string, firstName: string, lastName: string, phone: string | undefined) => Promise.resolve(),
  addAnonymAccountToCache: (userid: string, firstName: string, lastName: string) => {},
  deleteUser: (user: DBUser) => Promise.resolve(),
  isAdmin: () => false,
  isLoggedIn: () => false,
  countIncomingBookings: 0,
  isUserExisting: (firstname: string, lastname: string) => false,
  allUsers: [],
  updateUser: (user:any) => Promise.resolve(),
  adminHouses: []
};

const AuthContext = React.createContext<IAuthContextProps>(defaultContextState);
// Define our own custom convinent hook
export const useAuthContext = () => useContext(AuthContext);



export const AuthProvider: React.FC = (props) => {
  const [authState, setAuthState] = useState<IAuthState>({user:null, userDetails: null, pending: true, admin: false, adminHouses: []});
  const [users, setUsers] = useState<DBUser[]>([]);
  const [countIncomingBookings, setCountIncomingBookings] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchAllUsers = async () => {
    const users = await getAllUsers();
    const userArray: DBUser[] = Array.from(users.values());
    userArray.sort((a, b) => a.lastname.localeCompare(b.lastname));
    setUsers(userArray);
  }

  useEffect(() => {
    let unsubscribeIncomingReq: Unsubscribe | undefined = undefined;

    const getBookingRequests = async(adminHouses: HouseName[] = []) => {
      const constraints = [
        where("status", "in", [ReservationStatus.BOOKING_REQUESTED, ReservationStatus.CANCEL_REQUESTED])
      ];

      // If admin houses are specified, filter by those houses
      if (adminHouses && adminHouses.length > 0) {
        constraints.push(where("houseName", "in", adminHouses));
      }

      const q = query(
        collection(fireDb, "/reservations"),
        ...constraints
      );

      // real-time listener for incoming booking requests
      unsubscribeIncomingReq = onSnapshot(q, (querySnapshot) => {
        console.log(`New booking request incoming ${querySnapshot.size}`);
        setCountIncomingBookings(querySnapshot.size);
      });

      // for initial count when loading page
      const count = await getIncomingRequests(adminHouses);
      console.log(`Existing booking requests: ${count}`)
      setCountIncomingBookings(count);
    }

    const unsubscribeAuthState = fireAuth.onAuthStateChanged(async (user) => {
      let admin = false;
      let userDetails = null;
      let adminHouses: HouseName[] = [];
      if (user) {
        try {
          userDetails = await getUserDetails(user.uid);
          if (!userDetails) {
            throw Error("User is not consistent")
          }
          const adminClaims = await getAdminClaims(user);
          if (adminClaims && adminClaims.admin) {
            admin = true;
            adminHouses = adminClaims.houses || [];
            fetchAllUsers();
            getBookingRequests(adminHouses);
          }
        } catch (error) {
          console.error("Internal error during login:" + error);
          user = null;
          userDetails = null;
          admin = false;
          adminHouses = [];
        }
      }
      setAuthState({user, userDetails, pending:false, admin, adminHouses})
      if (user) {
        if (location.state) {
          const { from } = location.state as LocationState;
          navigate(from);
        } else {
          navigate("/");
        }
      }
    })

    return () => {
      unsubscribeAuthState()
      if (unsubscribeIncomingReq) {
        unsubscribeIncomingReq();
      }
    };
  }, []);

  const isUserExisting = (firstname: string, lastname: string) => {
    return users.findIndex((user => user.firstname == firstname.trim() && user.lastname == lastname.trim())) >= 0
  }

  const logout = async () => {
    await signOut(fireAuth);
    // => onAuthStateChanged should be called
  };

  const login = async (email: string, password: string): Promise<void> => {
    await setPersistence(fireAuth, browserLocalPersistence);
    // onAuthStateChanged will be called after this.
    await signInWithEmailAndPassword(fireAuth, email, password);
  };

  const registerAccount = async (email: string, password: string, firstName: string, lastName: string, phone: string | undefined): Promise<UserRecord> => {
    const data = {
      email: email,
      emailVerified: false,
      password: password,
      firstName: firstName,
      lastName: lastName,
      disabled: false,
      phone: phone
    };
    const createUsers = httpsCallable(fireFunctions, "createUser");
    const resp = await createUsers(data);
    const newUser: UserRecord = (resp.data as any).user;
    // this is called when a new user is created without booking process => bookingCount is 0
    const newUsers = [...users, {userid: newUser.uid, firstname: firstName, lastname: lastName, lastBooking: undefined, bookingCount: 0}];
    newUsers.sort((a, b) => a.firstname.localeCompare(b.firstname));
    setUsers(newUsers)
    return newUser;
  };

  // the anonymous account itself is created on the server together with the booking.
  const addAnonymAccountToCache = (userid: string, firstName: string, lastName: string) => {
    //this code is only executed when a booking is processed. so the bookingCount can be set to 1 for the cached user.
    const newUsers = [...users, {userid: userid, firstname: firstName, lastname: lastName, lastBooking: Timestamp.fromMillis(dateToUTC(new Date())), bookingCount: 1}];
    newUsers.sort((a, b) => a.firstname.localeCompare(b.firstname));
    setUsers(newUsers)
  };

  const isAdmin = (): boolean => {
    if (authState.user != null && authState.admin === true) {
      return true;
    }
    return false;
  };

  const deleteUser = async(user: DBUser):Promise<void> => {
    const data = {
      userid: user.userid
    };
    const deleteUser = httpsCallable(fireFunctions, "deleteUser");
    await deleteUser(data);
    await rescueDeletedUser(user);
    const idx = users.findIndex(u => u.userid === user.userid);
    const newUsers = cloneDeep(users);
    newUsers.splice(idx,1);
    setUsers(newUsers);
  }

  const updateUser = async(user: any): Promise<void> => {
    const updateUser = httpsCallable(fireFunctions, "updateUser");
    await updateUser(user);
    const idx = users.findIndex(u => u.userid === user.userid);
    const newUsers = cloneDeep(users);
    newUsers[idx] = {...newUsers[idx], firstname: user.firstName, lastname: user.lastName}
    setUsers(newUsers);
  }

  const isLoggedIn = (): boolean => {
    return authState.user != null;
  };

  if (authState.pending) {
    return <div className="bg-slate-50"></div>;
  }

  const contextValue = {
    user: authState.user,
    userDetails: authState.userDetails,
    isLoggedIn: isLoggedIn,
    isAdmin: isAdmin,
    login: login,
    logout: logout,
    registerAccount: registerAccount,
    addAnonymAccountToCache: addAnonymAccountToCache,
    allUsers: users,
    countIncomingBookings: countIncomingBookings,
    isUserExisting: isUserExisting,
    deleteUser: deleteUser,
    updateUser: updateUser,
    adminHouses: authState.adminHouses
  };

  return <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>;
};
